// Client-side, offline board generator — a faithful JS port of the backend
// `game_logic.py`. Lets the game generate a guaranteed-solvable board with
// zero network access (Offline Play). Boards are NOT seed-matched to the
// backend (we use Math.random); they only need to be the same LAYOUT family
// and 100% solvable. When the player is back online, results sync via
// /api/game/sync.

// 67 fitness-themed tile categories (identical set to backend TILE_TYPES).
export const TILE_TYPES = [
  // Cardio (12)
  "fit_run", "fit_bike", "fit_row", "fit_jumprope", "fit_swim", "fit_hike",
  "fit_walk", "fit_dance", "fit_box", "fit_skate", "fit_sprint", "fit_stairs",
  // Strength (10)
  "str_dumbbell", "str_barbell", "str_kettlebell", "str_squat", "str_bench",
  "str_deadlift", "str_curl", "str_pushup", "str_pullup", "str_plank",
  // Mind / flexibility (4)
  "mind_yoga", "mind_meditate", "mind_stretch", "mind_balance",
  // Nutrition & hydration (5)
  "nut_apple", "nut_water", "nut_shake", "nut_greens", "nut_banana",
  // Rewards / vibe (5)
  "rew_flame", "rew_medal", "rew_heart", "rew_bolt", "rew_target",
  // Bonus / vibe (4)
  "bonus_star", "bonus_crown", "bonus_gift", "bonus_diamond",
  // Food / nutrition extras (6)
  "food_cherry", "food_carrot", "food_egg", "food_beef", "food_soup",
  "food_coffee",
  // Mind extra (1)
  "sparkle_zen",
  // Plate-dot strength tiles (9)
  "plate_1", "plate_2", "plate_3", "plate_4", "plate_5",
  "plate_6", "plate_7", "plate_8", "plate_9",
  // Equipment dot tiles (9)
  "eq_foamroll", "eq_dumbbells", "eq_resistband", "eq_grippers",
  "eq_jumpropes", "eq_waterbottles", "eq_yogamats",
  "eq_kettlebells_small", "eq_shakers",
  // Honor tiles (2)
  "honor_north", "honor_white",
];

const P = (x, y, z) => ({ x, y, z });
const idiv = (a, b) => Math.floor(a / b);
const rangeArr = (lo, hi) => {
  const out = [];
  for (let i = lo; i < hi; i++) out.push(i);
  return out;
};

// ---------- Layout position builders (ported 1:1 from game_logic.py) --------
function _simple() {
  const p = [];
  for (let y = 0; y < 4; y++)
    for (let x = 0; x < 6; x++) {
      if ((x === 2 && y === 1) || (x === 3 && y === 2)) continue;
      p.push(P(x, y, 0));
    }
  for (let y = 1; y < 4; y++) for (let x = 1; x < 5; x++) p.push(P(x, y, 1));
  for (let y = 1; y < 3; y++) for (let x = 2; x < 5; x++) p.push(P(x, y, 2));
  p.push(P(2, 1, 3), P(3, 1, 3), P(4, 1, 3), P(3, 2, 3));
  return p;
}

function _pyramid() {
  const p = [];
  const layers = [[0, 7, 0], [1, 5, 1], [2, 3, 2], [3, 1, 3]];
  for (const [z, size, off] of layers)
    for (let y = 0; y < size; y++)
      for (let x = 0; x < size; x++) p.push(P(off + x, off + y, z));
  p.push(P(2.5, 3, 3), P(3.5, 3, 3));
  return p;
}

function _dumbbell() {
  const p = [];
  for (let y = 0; y < 5; y++) for (let x = 0; x < 4; x++) p.push(P(x, y, 0));
  for (let y = 2; y < 4; y++) for (let x = 4; x < 12; x++) p.push(P(x, y, 0));
  for (let y = 0; y < 5; y++) for (let x = 12; x < 16; x++) p.push(P(x, y, 0));
  for (let y = 1; y < 4; y++) for (let x = 1; x < 3; x++) p.push(P(x, y, 1));
  for (let y = 1; y < 4; y++) for (let x = 13; x < 15; x++) p.push(P(x, y, 1));
  for (let x = 5; x < 11; x++) p.push(P(x, 2.5, 1));
  return p;
}

function _dumbbellSmall() {
  const p = [];
  for (let y = 0; y < 3; y++) for (let x = 0; x < 3; x++) p.push(P(x, y, 0));
  for (let x = 3; x < 7; x++) p.push(P(x, 1, 0));
  for (let y = 0; y < 3; y++) for (let x = 7; x < 10; x++) p.push(P(x, y, 0));
  p.push(P(1, 1, 1));
  p.push(P(8, 1, 1));
  for (let x = 3; x < 7; x++) p.push(P(x, 0, 0));
  for (let x = 3; x < 7; x++) p.push(P(x, 2, 0));
  p.push(P(4, 1, 1), P(5, 1, 1), P(4, 0, 1), P(5, 2, 1));
  return p;
}

function _kettlebell() {
  const p = [];
  for (let y = 0; y < 2; y++) for (let x = 2; x < 4; x++) p.push(P(x, y, 0));
  const body = [[2, [1, 5]], [3, [0, 6]], [4, [0, 6]], [5, [0, 6]], [6, [0, 6]], [7, [1, 5]]];
  for (const [y, [lo, hi]] of body) for (let x = lo; x < hi; x++) p.push(P(x, y, 0));
  for (let y = 3; y < 6; y++) for (let x = 1; x < 5; x++) p.push(P(x, y, 1));
  for (let x = 2; x < 4; x++) p.push(P(x, 4, 2));
  return p;
}

function _plate() {
  const p = [];
  for (let y = 0; y < 8; y++)
    for (let x = 0; x < 7; x++) {
      if ((x === 0 || x === 6) && (y === 0 || y === 7)) continue;
      if (x === 3 && y >= 3 && y <= 4) continue;
      p.push(P(x, y, 0));
    }
  for (let y = 2; y < 6; y++)
    for (let x = 2; x < 5; x++) {
      if (x === 3 && y >= 3 && y <= 4) continue;
      p.push(P(x, y, 1));
    }
  return p;
}

function _barbell() {
  const p = [];
  for (let y = 0; y < 6; y++) for (let x = 0; x < 3; x++) p.push(P(x, y, 0));
  for (let y = 2; y < 4; y++) for (let x = 3; x < 17; x++) p.push(P(x, y, 0));
  for (let y = 0; y < 6; y++) for (let x = 17; x < 20; x++) p.push(P(x, y, 0));
  for (let y = 1; y < 5; y++) for (let x = 0; x < 2; x++) p.push(P(x, y, 1));
  for (let y = 1; y < 5; y++) for (let x = 18; x < 20; x++) p.push(P(x, y, 1));
  for (let x = 4; x < 16; x++) p.push(P(x, 2.5, 1));
  return p;
}

function _treadmill() {
  const p = [];
  for (let y = 2; y < 6; y++) for (let x = 0; x < 10; x++) p.push(P(x, y, 0));
  for (let y = 3; y < 5; y++) for (let x = 1; x < 9; x++) p.push(P(x, y, 1));
  for (let y = 0; y < 2; y++) for (let x = 7; x < 10; x++) p.push(P(x, y, 0));
  for (let x = 7; x < 10; x++) p.push(P(x, 0, 1));
  for (let x = 3; x < 7; x++) p.push(P(x, 3.5, 2));
  return p;
}

function _pullupBar() {
  const p = [];
  for (let y = 0; y < 6; y++) for (let x = 0; x < 2; x++) p.push(P(x, y, 0));
  for (let y = 0; y < 6; y++) for (let x = 10; x < 12; x++) p.push(P(x, y, 0));
  for (let x = 2; x < 10; x++) p.push(P(x, 0, 0));
  for (let x = 2; x < 10; x++) p.push(P(x, 1, 0));
  for (let y = 1; y < 5; y++) { p.push(P(0, y, 1)); p.push(P(11, y, 1)); }
  for (let x = 3; x < 9; x++) p.push(P(x, 0.5, 1));
  p.push(P(4, 0.5, 2), P(7, 0.5, 2));
  return p;
}

function _squatRack() {
  const p = [];
  for (let y = 0; y < 8; y++) for (let x = 0; x < 2; x++) p.push(P(x, y, 0));
  for (let y = 0; y < 8; y++) for (let x = 8; x < 10; x++) p.push(P(x, y, 0));
  for (let x = 0; x < 10; x++) p.push(P(x, 8, 0));
  for (let x = 2; x < 8; x++) p.push(P(x, 0, 0));
  for (let x = 2; x < 8; x++) p.push(P(x, 3, 0));
  for (let x = 1; x < 9; x++) p.push(P(x, 3, 1));
  for (let y = 2; y < 5; y++) { p.push(P(1, y, 1)); p.push(P(8, y, 1)); }
  p.push(P(0, 1, 2), P(9, 1, 2), P(4, 3, 2), P(5, 3, 2), P(4, 3, 3), P(5, 3, 3));
  return p;
}

function _benchPress() {
  const p = [];
  for (let y = 3; y < 5; y++) for (let x = 2; x < 8; x++) p.push(P(x, y, 0));
  for (let y = 5; y < 7; y++) { p.push(P(2, y, 0)); p.push(P(7, y, 0)); }
  for (let y = 0; y < 3; y++) { p.push(P(0, y, 0)); p.push(P(9, y, 0)); }
  for (let x = 0; x < 10; x++) p.push(P(x, 2, 0));
  for (let y = 1; y < 4; y++) { p.push(P(0, y, 1)); p.push(P(9, y, 1)); }
  for (let x = 3; x < 7; x++) p.push(P(x, 3, 1));
  for (let x = 3; x < 7; x++) p.push(P(x, 4, 1));
  p.push(P(0, 2, 2), P(9, 2, 2));
  return p;
}

function _track() {
  const p = [];
  for (let x = 0; x < 12; x++) { p.push(P(x, 0, 0)); p.push(P(x, 5, 0)); }
  for (let y = 1; y < 5; y++) { p.push(P(0, y, 0)); p.push(P(11, y, 0)); }
  for (let x = 1; x < 11; x++) { p.push(P(x, 1, 0)); p.push(P(x, 4, 0)); }
  for (let y = 2; y < 4; y++) { p.push(P(0, y, 1)); p.push(P(11, y, 1)); }
  p.push(P(5, 2, 1), P(6, 2, 1), P(5, 3, 1), P(6, 3, 1));
  return p;
}

function _gymTemple() {
  const p = [];
  for (let y = 0; y < 9; y++) for (let x = 0; x < 7; x++) p.push(P(x, y, 0));
  for (let y = 1; y < 8; y++) for (let x = 1; x < 6; x++) p.push(P(x, y, 1));
  for (let y = 2; y < 6; y++) for (let x = 2; x < 5; x++) p.push(P(x, y, 2));
  for (const y of [3, 4, 5]) for (const x of [2, 4]) p.push(P(x, y, 3));
  return p;
}

function _gymFortress() {
  const p = [];
  for (let y = 0; y < 9; y++) for (let x = 0; x < 7; x++) p.push(P(x, y, 0));
  for (let y = 1; y < 8; y++) for (let x = 1; x < 6; x++) p.push(P(x, y, 1));
  for (let y = 2; y < 6; y++) for (let x = 2; x < 5; x++) p.push(P(x, y, 2));
  for (let y = 3; y < 6; y++) { p.push(P(2.5, y, 3)); p.push(P(3.5, y, 3)); }
  return p;
}

function _ring() {
  const p = [];
  for (let y = 0; y < 6; y++)
    for (let x = 0; x < 8; x++) {
      if (x >= 2 && x <= 5 && y >= 2 && y <= 3) continue;
      p.push(P(x, y, 0));
    }
  for (let y = 1; y < 5; y++)
    for (let x = 1; x < 7; x++) {
      if (x >= 3 && x <= 4 && y >= 2 && y <= 3) continue;
      p.push(P(x, y, 1));
    }
  p.push(P(1, 1, 2), P(6, 1, 2), P(1, 4, 2), P(6, 4, 2));
  return p;
}

function _stairs() {
  const p = [];
  for (let y = 0; y < 5; y++)
    for (let x = 0; x < 8; x++) {
      if (y >= 3 && x <= 7 - y) continue;
      p.push(P(x, y, 0));
    }
  for (let i = 0; i < 5; i++) {
    p.push(P(2 + i, 1 + idiv(i, 2), 1));
    p.push(P(3 + i, 1 + idiv(i, 2), 1));
  }
  for (let i = 0; i < 3; i++) {
    p.push(P(4 + i, 1, 2));
    p.push(P(5 + i, 1, 2));
  }
  p.push(P(5, 1, 3), P(6, 1, 3));
  return p;
}

function _fromPattern(pattern, z, mirrorRight) {
  const p = [];
  pattern.forEach((row, y) => {
    for (let x = 0; x < row.length; x++) {
      if (row[x] === "X") {
        p.push(P(x, y, z));
        if (mirrorRight) p.push(P(mirrorRight - x, y, z));
      }
    }
  });
  return p;
}

function _heart() {
  const pattern = [
    "  XX  XX  ",
    " XXXXXXXX ",
    " XXXXXXXX ",
    "XXXXXXXXXX",
    " XXXXXXXX ",
    "  XXXXXX  ",
    "   XXXX   ",
    "    XX    ",
  ];
  const p = _fromPattern(pattern, 0);
  for (let y = 1; y < 4; y++)
    for (let x = 2; x < 8; x++) {
      if ((y === 1 && (x === 4 || x === 5)) || (y === 2 && (x === 4 || x === 5))) continue;
      p.push(P(x, y, 1));
    }
  p.push(P(3, 1, 2), P(6, 1, 2), P(4, 2, 2), P(5, 2, 2));
  return p;
}

function _butterfly() {
  const patternLeft = ["XXXX ", "XXXXX", "XXXXX", "XXXX ", "XXX  "];
  const p = [];
  patternLeft.forEach((row, y) => {
    for (let x = 0; x < row.length; x++) if (row[x] === "X") p.push(P(x, y, 0));
  });
  patternLeft.forEach((row, y) => {
    for (let x = 0; x < row.length; x++) if (row[x] === "X") p.push(P(10 - x, y, 0));
  });
  for (let y = 0; y < 5; y++) p.push(P(5, y, 0));
  for (let y = 1; y < 4; y++) { p.push(P(1, y, 1)); p.push(P(9, y, 1)); }
  p.push(P(5, 1, 2), P(5, 3, 2));
  return p;
}

function _bridge() {
  const p = [];
  for (let y = 2; y < 4; y++) for (let x = 0; x < 10; x++) p.push(P(x, y, 0));
  for (let x = 2; x < 8; x++) p.push(P(x, 1, 0));
  for (let y = 4; y < 7; y++) {
    p.push(P(1, y, 0)); p.push(P(2, y, 0)); p.push(P(7, y, 0)); p.push(P(8, y, 0));
  }
  for (let x = 0; x < 10; x++) p.push(P(x, 2, 1));
  p.push(P(4, 1, 2), P(5, 1, 2));
  return p;
}

function _fish() {
  const pattern = [
    "  XXXXXX  X",
    " XXXXXXXXX ",
    "XXXXXXXXXXX",
    " XXXXXXXXX ",
    "  XXXXXX  X",
  ];
  const p = [];
  pattern.forEach((row, y) => {
    for (let x = 0; x < row.length; x++) if (row[x] === "X") p.push(P(x, y, 0));
  });
  for (let y = 1; y < 4; y++) for (let x = 3; x < 8; x++) p.push(P(x, y, 1));
  p.push(P(2, 2, 2), P(4, 2, 2));
  return p;
}

function _starterTall() {
  const p = [];
  for (let y = 0; y < 6; y++) for (let x = 0; x < 4; x++) p.push(P(x, y, 0));
  return p;
}

function _column() {
  const p = [];
  for (let y = 0; y < 8; y++) for (let x = 0; x < 5; x++) p.push(P(x, y, 0));
  for (let y = 2; y < 6; y++) for (let x = 1; x < 4; x++) p.push(P(x, y, 1));
  return p;
}

function _tower() {
  const p = [];
  for (let y = 0; y < 8; y++) for (let x = 0; x < 6; x++) p.push(P(x, y, 0));
  for (let y = 2; y < 6; y++) for (let x = 1; x < 5; x++) p.push(P(x, y, 1));
  for (let y = 3; y < 5; y++) for (let x = 2; x < 4; x++) p.push(P(x, y, 2));
  return p;
}

function _diamond() {
  const p = [];
  const rows = [[3, 3], [2, 4], [1, 5], [0, 6], [0, 6], [0, 6], [1, 5], [2, 4], [3, 3]];
  rows.forEach(([lo, hi], y) => { for (let x = lo; x <= hi; x++) p.push(P(x, y, 0)); });
  for (let y = 3; y < 6; y++) for (let x = 2; x < 5; x++) p.push(P(x, y, 1));
  p.push(P(3, 4, 2), P(3, 3, 2));
  return p;
}

function _cross() {
  const p = [];
  for (let y = 2; y < 5; y++) for (let x = 0; x < 7; x++) p.push(P(x, y, 0));
  for (let y = 0; y < 7; y++)
    for (let x = 2; x < 5; x++) {
      if (y >= 2 && y <= 4) continue;
      p.push(P(x, y, 0));
    }
  for (let y = 2; y < 5; y++) for (let x = 2; x < 5; x++) p.push(P(x, y, 1));
  p.push(P(3, 3, 2), P(3, 2, 2));
  return p;
}

function _doublePyramid() {
  const p = [];
  const upper = [[0, 4, rangeArr(0, 7)], [1, 3, rangeArr(1, 6)], [2, 2, rangeArr(2, 5)]];
  for (const [z, yMid, cols] of upper) {
    for (const x of cols) p.push(P(x, yMid, z));
    if (z === 0) for (const x of cols) p.push(P(x, yMid - 1, z));
  }
  const lower = [[0, 6, rangeArr(0, 7)], [1, 7, rangeArr(1, 6)], [2, 8, rangeArr(2, 5)]];
  for (const [z, yMid, cols] of lower) {
    for (const x of cols) p.push(P(x, yMid, z));
    if (z === 0) for (const x of cols) p.push(P(x, yMid + 1, z));
  }
  p.push(P(3, 4.5, 3), P(3, 5.5, 3));
  return p;
}

function _obelisk() {
  const p = [];
  for (let y = 0; y < 9; y++) for (let x = 0; x < 6; x++) p.push(P(x, y, 0));
  for (let y = 1; y < 8; y++) for (let x = 1; x < 5; x++) p.push(P(x, y, 1));
  for (let y = 2; y < 7; y++) for (let x = 2; x < 4; x++) p.push(P(x, y, 2));
  return p;
}

function _dwHexagon() {
  const p = [];
  const rows = [[2, 4], [1, 5], [0, 6], [0, 6], [0, 6], [0, 6], [0, 6], [1, 5], [2, 4]];
  rows.forEach(([lo, hi], y) => { for (let x = lo; x <= hi; x++) p.push(P(x, y, 0)); });
  for (let y = 2; y < 7; y++)
    for (let x = 1; x < 6; x++) {
      if ((y === 2 || y === 6) && (x === 1 || x === 5)) continue;
      p.push(P(x, y, 1));
    }
  const cap = [[3, 3], [4, 3], [5, 3], [4, 2], [4, 4]]; // (y,x)
  for (const [y, x] of cap) p.push(P(x, y, 2));
  return p;
}

function _dwHourglass() {
  const p = [];
  const top = [[0, 6], [1, 5], [2, 4], [3, 4]];
  const bot = [[3, 4], [2, 4], [1, 5], [0, 6]];
  top.forEach(([lo, hi], y) => { for (let x = lo; x <= hi; x++) p.push(P(x, y, 0)); });
  for (let x = 3; x <= 4; x++) p.push(P(x, 4, 0));
  bot.forEach(([lo, hi], i) => { const y = 5 + i; for (let x = lo; x <= hi; x++) p.push(P(x, y, 0)); });
  for (let x = 1; x < 6; x++) { p.push(P(x, 1, 1)); p.push(P(x, 7, 1)); }
  p.push(P(3, 4, 1), P(4, 4, 1));
  return p;
}

function _dwLotus() {
  const p = [];
  const base = [[2, 4], [1, 5], [0, 6], [1, 5], [2, 4]];
  base.forEach(([lo, hi], i) => { const y = 2 + i; for (let x = lo; x <= hi; x++) p.push(P(x, y, 0)); });
  for (const x of [2, 3, 4]) { p.push(P(x, 0, 0)); p.push(P(x, 1, 0)); }
  for (const x of [2, 3, 4]) { p.push(P(x, 7, 0)); p.push(P(x, 8, 0)); }
  for (const y of [3, 4, 5]) for (const x of [2, 3, 4]) p.push(P(x, y, 1));
  p.push(P(3, 4, 2), P(3, 3, 2));
  return p;
}

function _dwDumbbellTower() {
  const p = [];
  for (let y = 0; y < 3; y++) for (let x = 0; x < 6; x++) p.push(P(x, y, 0));
  for (let y = 3; y < 6; y++) for (const x of [2, 3]) p.push(P(x, y, 0));
  for (let y = 6; y < 9; y++) for (let x = 0; x < 6; x++) p.push(P(x, y, 0));
  for (const y of [0, 1, 7, 8]) for (let x = 1; x < 5; x++) p.push(P(x, y, 1));
  for (const y of [0, 8]) for (const x of [2, 3]) p.push(P(x, y, 2));
  return p;
}

function _dwFlame() {
  const p = [];
  const layout = [
    [8, [0, 6]], [7, [0, 6]], [6, [1, 5]], [5, [1, 5]],
    [4, [1, 4]], [3, [2, 4]], [2, [2, 3]], [1, [3, 3]], [0, [3, 3]],
  ];
  for (const [y, [lo, hi]] of layout) for (let x = lo; x <= hi; x++) p.push(P(x, y, 0));
  const inner = [[7, [1, 5]], [6, [1, 5]], [5, [2, 4]], [4, [2, 4]], [3, [3, 3]]];
  for (const [y, [lo, hi]] of inner) for (let x = lo; x <= hi; x++) p.push(P(x, y, 1));
  p.push(P(3, 6, 2), P(3, 7, 2));
  return p;
}

function _dwMountain() {
  const p = [];
  const steps = [
    [8, [0, 6]], [7, [0, 6]], [6, [1, 5]], [5, [1, 5]], [4, [2, 4]],
    [3, [2, 4]], [2, [3, 4]], [1, [3, 3]], [0, [3, 3]],
  ];
  for (const [y, [lo, hi]] of steps) for (let x = lo; x <= hi; x++) p.push(P(x, y, 0));
  const ridge = [[7, [1, 5]], [6, [2, 4]], [5, [2, 4]], [4, [3, 3]], [3, [3, 3]]];
  for (const [y, [lo, hi]] of ridge) for (let x = lo; x <= hi; x++) p.push(P(x, y, 1));
  p.push(P(3, 1, 2), P(3, 2, 2), P(4, 5, 2), P(3, 5, 2));
  return p;
}

function _kettlebellPair() {
  const p = [];
  for (const off of [0, 4]) {
    for (const y of [0, 1]) p.push(P(off + 1, y, 0));
    for (let y = 2; y < 9; y++) for (let x = off; x < off + 3; x++) p.push(P(x, y, 0));
    for (let y = 3; y < 8; y++) for (let x = off; x < off + 3; x++) p.push(P(x, y, 1));
    for (let y = 4; y < 7; y++) p.push(P(off + 1, y, 2));
  }
  return p;
}

function _spartanShield() {
  const p = [];
  const rows = [[2, 4], [1, 5], [0, 6], [0, 6], [0, 6], [0, 6], [0, 6], [1, 5], [2, 4]];
  rows.forEach(([lo, hi], y) => { for (let x = lo; x <= hi; x++) p.push(P(x, y, 0)); });
  for (let y = 2; y < 7; y++) for (let x = 1; x < 6; x++) p.push(P(x, y, 1));
  for (let y = 3; y < 6; y++) for (let x = 2; x < 5; x++) p.push(P(x, y, 2));
  p.push(P(3, 3, 3), P(2, 4, 3), P(3, 4, 3), P(4, 4, 3), P(3, 5, 3));
  return p;
}

export const LAYOUT_BUILDERS = {
  simple: _simple,
  pyramid: _pyramid,
  dumbbell: _dumbbell,
  dumbbell_small: _dumbbellSmall,
  kettlebell: _kettlebell,
  kettlebell_pair: _kettlebellPair,
  spartan_shield: _spartanShield,
  plate: _plate,
  treadmill: _treadmill,
  pullup_bar: _pullupBar,
  squat_rack: _squatRack,
  bench_press: _benchPress,
  track: _track,
  gym_temple: _gymTemple,
  gym_fortress: _gymFortress,
  ring: _ring,
  stairs: _stairs,
  heart: _heart,
  butterfly: _butterfly,
  bridge: _bridge,
  fish: _fish,
  starter_tall: _starterTall,
  column: _column,
  tower: _tower,
  diamond: _diamond,
  cross: _cross,
  obelisk: _obelisk,
  double_pyramid: _doublePyramid,
  hexagon: _dwHexagon,
  hourglass: _dwHourglass,
  lotus: _dwLotus,
  dumbbell_tower: _dwDumbbellTower,
  flame: _dwFlame,
  mountain: _dwMountain,
};

// ---------- Solvability + forward-sim builder -------------------------------
function _shuffle(arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// Selectability by INDEX against an on-board Set (mirrors backend _is_sel_idx).
function _isSelIdx(idx, onBoard, positions) {
  const p = positions[idx];
  const { x: px, y: py, z: pz } = p;
  let leftBlocked = false;
  let rightBlocked = false;
  for (const j of onBoard) {
    if (j === idx) continue;
    const q = positions[j];
    if (q.z > pz && Math.abs(q.x - px) < 0.6 && Math.abs(q.y - py) < 0.6) return false;
    if (q.z === pz && Math.abs(q.y - py) < 0.6) {
      const dx = q.x - px;
      if (Math.abs(dx - 1) < 0.6) rightBlocked = true;
      else if (Math.abs(dx + 1) < 0.6) leftBlocked = true;
    }
  }
  return !(leftBlocked && rightBlocked);
}

function _isSelectable(tile, tiles) {
  const { x, y, z } = tile;
  for (const t of tiles) {
    if (t.removed || t.tile_id === tile.tile_id) continue;
    if (t.z > z && Math.abs(t.x - x) < 0.6 && Math.abs(t.y - y) < 0.6) return false;
  }
  let left = false;
  let right = false;
  for (const t of tiles) {
    if (t.removed || t.tile_id === tile.tile_id || t.z !== z) continue;
    if (Math.abs(t.y - y) >= 0.6) continue;
    if (Math.abs(t.x - (x - 1)) < 0.6) left = true;
    if (Math.abs(t.x - (x + 1)) < 0.6) right = true;
  }
  return !left || !right;
}

function _countAvailablePairs(tiles) {
  const free = tiles.filter((t) => _isSelectable(t, tiles));
  const byType = {};
  for (const t of free) byType[t.tile_type] = (byType[t.tile_type] || 0) + 1;
  return Object.values(byType).reduce((s, c) => s + Math.floor(c / 2), 0);
}

function _forwardSimSolvable(positions, maxAttempts = 30) {
  let n = positions.length;
  if (n % 2 === 1) { positions = positions.slice(0, -1); n -= 1; }

  const tryOnce = () => {
    const onBoard = new Set();
    for (let i = 0; i < n; i++) onBoard.add(i);
    const pairs = [];
    while (onBoard.size > 0) {
      const selectables = [];
      for (const i of onBoard) if (_isSelIdx(i, onBoard, positions)) selectables.push(i);
      if (selectables.length < 2) return null;
      let maxZ = -Infinity;
      for (const i of selectables) if (positions[i].z > maxZ) maxZ = positions[i].z;
      const topPool = selectables.filter((i) => positions[i].z === maxZ);
      _shuffle(topPool);
      const a = topPool[0];
      const { x: ax, y: ay, z: az } = positions[a];
      const others = selectables.filter((i) => i !== a);
      const isAdjacent = (j) => {
        const q = positions[j];
        if (q.z !== az) return false;
        return Math.abs(q.y - ay) < 0.6 && Math.abs(Math.abs(q.x - ax) - 1) < 0.6;
      };
      const nonAdj = others.filter((i) => !isAdjacent(i));
      const pool = nonAdj.length ? nonAdj : others;
      _shuffle(pool);
      const b = pool[0];
      pairs.push([a, b]);
      onBoard.delete(a);
      onBoard.delete(b);
    }
    return pairs;
  };

  let pairs = null;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    pairs = tryOnce();
    if (pairs) break;
  }
  if (!pairs) return null;

  const palette = _shuffle([...TILE_TYPES]);
  const tiles = new Array(positions.length).fill(null);
  pairs.forEach(([a, b], k) => {
    const ttype = palette[k % palette.length];
    for (const idx of [a, b]) {
      const p = positions[idx];
      tiles[idx] = { tile_id: `t${idx}`, x: p.x, y: p.y, z: p.z, tile_type: ttype };
    }
  });
  return tiles.filter(Boolean);
}

// Public: generate a guaranteed-solvable board for a layout name.
export function buildLayout(layoutName) {
  const builder = LAYOUT_BUILDERS[layoutName] || _simple;
  let positions = builder();
  const MAX_Z = 3;
  positions = positions.map((p) => (p.z > MAX_Z ? { ...p, z: MAX_Z } : p));
  const seen = new Set();
  const deduped = [];
  for (const p of positions) {
    const key = `${p.x}_${p.y}_${p.z}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(p);
  }
  positions = deduped;
  if (positions.length % 2 !== 0) positions = positions.slice(0, -1);

  let best = _forwardSimSolvable(positions);
  if (!best) best = _forwardSimSolvable(positions, 60);
  const targetScore = Math.max(4, Math.floor(positions.length / 18));
  let bestScore = _countAvailablePairs(best);
  if (bestScore >= targetScore) return best;
  for (let attempt = 1; attempt < 8; attempt++) {
    const cand = _forwardSimSolvable(positions);
    if (!cand) continue;
    const cs = _countAvailablePairs(cand);
    if (cs > bestScore) {
      bestScore = cs;
      best = cand;
      if (cs >= targetScore) break;
    }
  }
  return best;
}
